import { takeLatest, call, put, select } from "redux-saga/effects";

import { routerActions } from "react-router-redux";

import { types, actions as recoveryPasswordActions } from "reducers/recoveryPassword";
import { types as typesToken } from "reducers/token";
import { actions as notificationActions } from "reducers/notification";
import { adjustIdFieldErrors, mapUserpassStep0ErrorsForRedux } from "util/form.js";
import * as i18nUtils from "util/i18n";
import { isSimulatedLoginToken } from "util/login";
import * as recovery from "middleware/recoveryPassword";
import { actions as loginActions } from "reducers/login";
import { types as fingerprintTypes } from "reducers/fingerprint";
import * as deviceUtils from "util/device";
import * as secureStorageUtils from "util/secureStorage";
import * as configUtils from "util/config";

const sagas = [
    takeLatest(types.RECOVERY_PASS_BACK_TO_STEP_1, handleGoToStep1),
    takeLatest(types.RECOVERY_PASS_PREVIEW_REQUEST, handleRecoveryPassPreview),
    takeLatest(types.RECOVERY_PASS_STEP1_REQUEST, handleRecoveryPassStep1),
    takeLatest(types.RECOVERY_PASS_STEP2_REQUEST, handleRecoveryPassStep2),
    takeLatest(types.RECOVERY_PASS_STEP3_REQUEST, handleRecoveryPassStep3),
    takeLatest(types.RECOVERY_USERPASS_STEP0_REQUEST, handleRecoveryUserpassStep0),
    takeLatest(types.RECOVERY_USERPASS_STEP1_REQUEST, handleRecoveryUserpassStep1),
    takeLatest(types.RECOVERY_USERPASS_STEP2_REQUEST, handleRecoveryUserpassStep2),
    takeLatest(types.RECOVERY_USERPASS_STEP3_REQUEST, handleRecoveryUserpassStep3),
    takeLatest(types.RECOVERY_USERPASS_BACK_TO_STEP_1, handleGoToUserpassStep1),
    takeLatest(types.RECOVERY_PASS_USERPASS_STEP1_REQUEST, handleRecoveryPassAndUserpassStep1),
    takeLatest(types.RECOVERY_PASS_USERPASS_STEP2_REQUEST, handleRecoveryPassAndUserpassStep2),
    takeLatest(types.RECOVERY_PASS_USERPASS_STEP3_REQUEST, handleRecoveryPassAndUserpassStep3),
];

export default sagas;

function* syncFormErrors(formikBag, errorsObject, mapForRedux) {
    const pathname = formikBag?.props?.location?.pathname ?? "";
    const isV3 = pathname.startsWith("/v3/");
    const payload = mapForRedux ? mapForRedux(errorsObject) : errorsObject;
    if (isV3) {
        yield put(recoveryPasswordActions.setFormFieldErrors(payload));
    } else {
        formikBag.setErrors(errorsObject);
    }
}

function* handleGoToStep1() {
    const pathname = yield select((s) => s.router?.location?.pathname || "");
    const step1Path = pathname.startsWith("/v3/") ? "/v3/recoveryPassword/step1" : "/recoveryPassword/step1";
    yield put(routerActions.push(step1Path));
}

function* handleRecoveryPassStep1({ sendChannel, userName, secondFactor, formikBag, exchangeToken }) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const { isMobileNative, location } = formikBag.props;
        let idDevice = null;
        if (window.app) {
            idDevice = window.app.getDeviceUUID();
        } else {
            idDevice = yield deviceUtils.getDeviceFingerprint();
        }
        const response = yield call(
            recovery.recoveryPassStep1,
            sendChannel,
            userName,
            secondFactor,
            exchangeToken,
            idDevice,
        );

        if (
            response.type === "W" ||
            (response.data.data._result !== null &&
                response.data.data._result !== undefined &&
                response.data.data._result !== "VALIDO")
        ) {
            yield* syncFormErrors(formikBag, adjustIdFieldErrors(response.data.data), null);
            if (response.data.data._result === "INVALIDO") {
                yield* syncFormErrors(
                    formikBag,
                    adjustIdFieldErrors({ secondFactor: i18nUtils.get("settings.token.password.invalid") }),
                    null,
                );
            } else if (response.data.data._result === "DISABLED") {
                yield put({
                    type: typesToken.CHECK_USER_ACTIVE_TOKEN_SUCCESS,
                    webTokenStatus: "blocked",
                });
                yield put(routerActions.push("/settings/token/recoverPass"));
            } else if (response.data.code === "API021W" || response.data.code === "COR050W") {
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put({ type: types.RECOVERY_PASS_FAILURE_REQUIRE_CAPTCHA });
            } else if (!isMobileNative) {
                const isStep1 =
                    location.pathname === "/recoveryPassword/step1" ||
                    location.pathname === "/v3/recoveryPassword/step1";
                if (!isStep1) {
                    yield put(
                        notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]),
                    );
                }
                yield* syncFormErrors(
                    formikBag,
                    adjustIdFieldErrors({ secondFactor: i18nUtils.get("token.otp.invalid") }),
                    null,
                );
            }
        } else {
            const { _exchangeToken } = response.data.data;
            yield put({ type: types.RECOVERY_PASS_STEP1_SUCCESS, exchangeToken: _exchangeToken });
            const step2Path = isV3Path ? "/v3/recoveryPassword/step2" : "/recoveryPassword/step2";
            yield put(routerActions.push(step2Path));
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

function* handleRecoveryPassStep2({ code, formikBag, exchangeToken }) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const response = yield call(recovery.recoveryPassStep2, code, exchangeToken);

        if (response.type === "W") {
            yield* syncFormErrors(formikBag, adjustIdFieldErrors(response.data.data), null);
            if (response.data.code !== "COR020W") {
                // exchangeToken expired, restart flow
                yield put({ type: types.CLEAN });
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put(routerActions.push(isV3Path ? "/v3/recoveryPassword/step2" : "/recoveryPassword/step2"));
            }
        } else {
            const { _exchangeToken, _resetCode } = response.data.data;
            yield put({
                type: types.RECOVERY_PASS_STEP2_SUCCESS,
                exchangeToken: _exchangeToken,
                resetCode: _resetCode,
            });
            const step3Path = isV3Path ? "/v3/recoveryPassword/step3" : "/recoveryPassword/step3";
            yield put(routerActions.push(step3Path));
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

function* handleRecoveryPassStep3({ newPassword, newPasswordConfirmation, resetCode, exchangeToken, formikBag }) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const response = yield call(
            recovery.recoveryPassStep3,
            newPassword,
            newPasswordConfirmation,
            resetCode,
            exchangeToken,
        );

        if (response.type === "W") {
            yield* syncFormErrors(formikBag, adjustIdFieldErrors(response.data.data), null);
            if (response.data.code !== "COR020W") {
                // exchangeToken expired, restart flow
                yield put({ type: types.CLEAN });
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put(routerActions.push(isV3Path ? "/v3/recoveryPassword/step2" : "/recoveryPassword/step2"));
            }
        } else {
            yield put(
                notificationActions.showNotification(i18nUtils.get("recoveryPassword.success.message"), "success", [
                    "login",
                ]),
            );
            yield put(routerActions.push(isV3Path ? "/v3/recoveryPassword/step4" : "/"));
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

function* handleRecoveryUserpassStep0({
    password,
    document,
    documentType,
    formikBag,
    isRecoveryPassAndUserpass,
    captcha,
}) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const response = yield call(
            recovery.recoveryUserpassStep0,
            password,
            document,
            documentType,
            isRecoveryPassAndUserpass,
            captcha,
        );
        if (response.data.data.showCaptcha) {
            yield put({ type: types.RECOVERY_PASS_FAILURE_REQUIRE_CAPTCHA });

            formikBag.setValues({
                documentType: documentType || "DNI",
                document: document || "",
                password: "",
                captcha: "",
            });
        }
        const { error } = response.data.data;
        if (error) {
            if (error === "login.credencialesbloqueadas.snackbar") {
                yield put(
                    notificationActions.showNotification(
                        configUtils.get("login.credencialesbloqueadas.snackbar"),
                        "error",
                        ["recoveryUserpass"],
                        i18nUtils.get("login.step2.password.blocked.tittle"),
                        false,
                    ),
                );
            } else if (error === "login.step1.blocked.disable.document" || error === "login.step1.blocked.document") {
                yield put(
                    notificationActions.showNotification(
                        i18nUtils.get(error),
                        "error",
                        ["recoveryUserpass"],
                        i18nUtils.get(`${error}.title`),
                        false,
                    ),
                );
            } else if (
                error === "login.step1.wrong.credentials" ||
                error === "login.step1.wrong.document" ||
                error === "login.step1.wrong.user" ||
                error === "recoveryUserpass.step0.wrong.password"
            ) {
                // Mensaje unificado anti user-enumeration: notificación genérica sin marcar
                // ni el campo "document" ni el campo "password".
                // Limpio fieldErrors previos (V3) y errores formik (V2) para no dejar visible
                // un error de campo de un intento anterior que diferencie documento vs password.
                yield put(recoveryPasswordActions.setFormFieldErrors({}));
                if (formikBag?.setErrors) {
                    formikBag.setErrors({});
                }
                yield put(
                    notificationActions.showNotification(i18nUtils.get("login.step1.wrong.credentials"), "error", [
                        "recoveryUserpass",
                    ]),
                );
            } else if (error === "login.step1.captcha.required") {
                yield* syncFormErrors(formikBag, { captcha: i18nUtils.get(error) }, mapUserpassStep0ErrorsForRedux);
            } else {
                yield* syncFormErrors(formikBag, { password: i18nUtils.get(error) }, mapUserpassStep0ErrorsForRedux);
            }
            formikBag.setValues({
                documentType,
                document: document || "",
                password: "",
                captcha: "",
            });
        } else {
            const {
                _userFirstName,
                _userFullName,
                lang,
                _userMaskedEmail,
                _userMaskedPhoneNumber,
            } = response.data.data;

            yield put({
                type: types.RECOVERY_USERPASS_STEP0_SUCCESS,
                userFirstName: _userFirstName,
                userFullName: _userFullName,
                userName: documentType + document,
                lang,
                userMaskedEmail: _userMaskedEmail,
                userMaskedPhone: _userMaskedPhoneNumber,
            });
            const step1Path = isV3Path ? "/v3/recoveryUserpass/step1" : "/recoveryUserpass/step1";
            yield put(routerActions.push(step1Path));
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        yield put({ type: types.RECOVERY_PASS_RESET_CAPTCHA, resetCaptcha: true });
        if (formikBag) {
            formikBag.setSubmitting(false);
        }
    }
}

function* handleRecoveryUserpassStep1({ sendChannel, userName, secondFactor, formikBag, isRecoveryPassAndUserpass }) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const { isMobileNative, location } = formikBag.props;
        let idDevice = null;
        if (window.app) {
            idDevice = window.app.getDeviceUUID();
        } else {
            idDevice = yield deviceUtils.getDeviceFingerprint();
        }
        const response = yield call(
            recovery.recoveryUserpassStep1,
            sendChannel,
            userName,
            secondFactor,
            isRecoveryPassAndUserpass,
            idDevice,
        );

        if (
            response.type === "W" ||
            (response.data.data._result !== null &&
                response.data.data._result !== undefined &&
                response.data.data._result !== "VALIDO")
        ) {
            yield* syncFormErrors(formikBag, adjustIdFieldErrors(response.data.data), null);

            if (response.data.data._result === "INVALIDO") {
                yield* syncFormErrors(
                    formikBag,
                    adjustIdFieldErrors({ secondFactor: i18nUtils.get("settings.token.password.invalid") }),
                    null,
                );
            } else if (response.data.data._result === "DISABLED") {
                yield put({
                    type: typesToken.CHECK_USER_ACTIVE_TOKEN_SUCCESS,
                    webTokenStatus: "blocked",
                });
                yield put(routerActions.push("/settings/token/recoverPass"));
            } else if (response.data.code === "API021W" || response.data.code === "COR050W") {
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put({ type: types.RECOVERY_PASS_FAILURE_REQUIRE_CAPTCHA });
            } else if (!isMobileNative) {
                const isStep1 =
                    location.pathname === "/recoveryUserpass/step1" ||
                    location.pathname === "/v3/recoveryUserpass/step1";
                if (!isStep1) {
                    yield put(
                        notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]),
                    );
                }
                yield* syncFormErrors(
                    formikBag,
                    adjustIdFieldErrors({ secondFactor: i18nUtils.get("token.otp.invalid") }),
                    null,
                );
            }
        } else {
            const step2Path = isV3Path ? "/v3/recoveryUserpass/step2" : "/recoveryUserpass/step2";
            yield put(routerActions.push(step2Path));
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

function* handleRecoveryUserpassStep2({ code, formikBag }) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const response = yield call(recovery.recoveryUserpassStep2, code);

        if (response.type === "W") {
            yield* syncFormErrors(formikBag, adjustIdFieldErrors(response.data.data), null);
            if (response.data.code !== "COR020W") {
                // exchangeToken expired, restart flow
                yield put({ type: types.CLEAN });
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put(routerActions.push(isV3Path ? "/v3/recoveryUserpass/step2" : "/recoveryUserpass/step2"));
            }
        } else {
            const { _exchangeToken, _resetCode } = response.data.data;

            yield put({
                type: types.RECOVERY_USERPASS_STEP2_SUCCESS,
                exchangeToken: _exchangeToken,
                resetCode: _resetCode,
            });
            const step3Path = isV3Path ? "/v3/recoveryUserpass/step3" : "/recoveryUserpass/step3";
            yield put(routerActions.push(step3Path));
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

function* handleRecoveryUserpassStep3({ newPassword, newPasswordConfirmation, resetCode, exchangeToken, formikBag }) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        let deviceToken = "";
        let fingerprintAuthToken = null;
        let fingerprintAuthTokenExists = true;

        try {
            fingerprintAuthToken = yield call(secureStorageUtils.get, "fingerprintAuthToken");
        } catch (error) {
            fingerprintAuthTokenExists = false;
        }

        if (fingerprintAuthTokenExists) {
            deviceToken = fingerprintAuthToken;
        }

        const response = yield call(
            recovery.recoveryUserpassStep3,
            newPassword,
            newPasswordConfirmation,
            resetCode,
            exchangeToken,
            deviceToken,
        );

        if (response.type === "W") {
            yield* syncFormErrors(formikBag, adjustIdFieldErrors({ password: response.data.data.pin }), null);
            if (response.data.code !== "COR020W") {
                // exchangeToken expired, restart flow
                yield put({ type: types.CLEAN });
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put(routerActions.push(isV3Path ? "/v3/recoveryUserpass/step2" : "/recoveryUserpass/step2"));
            }
        } else {
            if (fingerprintAuthTokenExists) {
                yield put({ type: fingerprintTypes.CLEAN_UP });
                yield put(loginActions.removeRememberedUser());
                yield put(loginActions.removeBiometricLoginShow());
                yield call(secureStorageUtils.remove, "showLogin");
                yield call(secureStorageUtils.remove, "documentType");
                yield call(secureStorageUtils.remove, "userFullName");
                yield call(secureStorageUtils.remove, "userFirstName");
            }

            yield put({ type: types.RECOVERY_PASS_SUCCESS });
            yield put(
                notificationActions.showNotification(i18nUtils.get("recoveryUserpass.success.message"), "success", [
                    "login",
                ]),
            );

            yield put(routerActions.push(isV3Path ? "/v3/login" : "/"));
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

function* handleGoToUserpassStep1() {
    const pathname = yield select((s) => s.router?.location?.pathname || "");
    const step1Path = pathname.startsWith("/v3/") ? "/v3/recoveryUserpass/step1" : "/recoveryUserpass/step1";
    yield put(routerActions.push(step1Path));
}

function* handleRecoveryPassAndUserpassStep1({ code, formikBag }) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const response = yield call(recovery.recoveryPassAndUserpassStep1, code);

        if (response.type === "W") {
            yield* syncFormErrors(formikBag, adjustIdFieldErrors(response.data.data), null);
            if (response.data.code !== "COR020W") {
                // exchangeToken expired, restart flow
                yield put({ type: types.CLEAN });
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put(routerActions.push(isV3Path ? "/v3/recoveryUserpass/step2" : "/recoveryUserpass/step2"));
            }
        } else {
            const { _exchangeToken, _resetCode } = response.data.data;

            yield put({
                type: types.RECOVERY_PASS_USERPASS_STEP1_SUCCESS,
                exchangeToken: _exchangeToken,
                resetCode: _resetCode,
            });

            yield put(
                routerActions.push(isV3Path ? "/v3/recoveryPassAndUserpass/step2" : "/recoveryPassAndUserpass/step2"),
            );
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

function* handleRecoveryPassAndUserpassStep2({
    newUserpass,
    newUserpassConfirmation,
    resetCode,
    exchangeToken,
    formikBag,
}) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const response = yield call(
            recovery.recoveryPassAndUserpassStep2,
            newUserpass,
            newUserpassConfirmation,
            resetCode,
            exchangeToken,
        );

        if (response.type === "W") {
            const adjusted = adjustIdFieldErrors(response.data.data);
            const errors = { ...adjusted };
            if (errors.newUserpass === undefined && errors.pin !== undefined) {
                errors.newUserpass = errors.pin;
            }
            if (errors.newUserpassConfirmation === undefined && errors.pinConfirmation !== undefined) {
                errors.newUserpassConfirmation = errors.pinConfirmation;
            }
            yield* syncFormErrors(formikBag, errors, null);
            if (response.data.code !== "COR020W") {
                // exchangeToken expired, restart flow
                yield put({ type: types.CLEAN });
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put(
                    routerActions.push(
                        isV3Path ? "/v3/recoveryPassAndUserpass/step2" : "/recoveryPassAndUserpass/step2",
                    ),
                );
            }
        } else {
            const { _exchangeToken } = response.data.data;

            yield put({
                type: types.RECOVERY_PASS_USERPASS_STEP2_SUCCESS,
                exchangeToken: _exchangeToken,
            });
            yield put(
                routerActions.push(isV3Path ? "/v3/recoveryPassAndUserpass/step3" : "/recoveryPassAndUserpass/step3"),
            );
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

function* handleRecoveryPassAndUserpassStep3({
    newPassword,
    newPasswordConfirmation,
    resetCode,
    exchangeToken,
    formikBag,
}) {
    const isV3Path = formikBag.props?.location?.pathname?.startsWith("/v3/");
    if (isV3Path) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        let deviceToken = "";
        let fingerprintAuthToken = null;
        let fingerprintAuthTokenExists = true;

        try {
            fingerprintAuthToken = yield call(secureStorageUtils.get, "fingerprintAuthToken");
        } catch (error) {
            fingerprintAuthTokenExists = false;
        }

        if (fingerprintAuthTokenExists) {
            deviceToken = fingerprintAuthToken;
        }

        const response = yield call(
            recovery.recoveryPassAndUserpassStep3,
            newPassword,
            newPasswordConfirmation,
            resetCode,
            exchangeToken,
            deviceToken,
        );

        if (response.type === "W") {
            yield* syncFormErrors(formikBag, adjustIdFieldErrors(response.data.data), null);
            if (response.data.code !== "COR020W") {
                // exchangeToken expired, restart flow
                yield put({ type: types.CLEAN });
                yield put(notificationActions.showNotification(response.data.message, "error", ["recoveryPassword"]));
                yield put(
                    routerActions.push(
                        isV3Path ? "/v3/recoveryPassAndUserpass/step2" : "/recoveryPassAndUserpass/step2",
                    ),
                );
            }
        } else {
            yield put({ type: types.RECOVERY_PASS_SUCCESS });
            yield put(
                notificationActions.showNotification(
                    i18nUtils.get("recoverPinAndPassword.success.message"),
                    "success",
                    ["login"],
                ),
            );

            if (fingerprintAuthTokenExists) {
                yield put({ type: fingerprintTypes.CLEAN_UP });
                yield put(loginActions.removeRememberedUser());
                yield put(loginActions.removeBiometricLoginShow());
                yield call(secureStorageUtils.remove, "showLogin");
                yield call(secureStorageUtils.remove, "documentType");
                yield call(secureStorageUtils.remove, "userFullName");
                yield call(secureStorageUtils.remove, "userFirstName");
            }

            yield put(routerActions.push(isV3Path ? "/v3/login" : "/"));
        }
    } finally {
        if (isV3Path) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
        formikBag.setSubmitting(false);
    }
}

// ARBCMFIMP-19158: cuando no se puede iniciar la recuperación porque el step 1 no tenía datos
// válidos (anti-enumeración, ARBCMFIMP-18419), reiniciamos el flujo y volvemos al step 1 de login
// con el mensaje genérico, en vez de dejar el botón "sin acción" y un 401 en consola.
function* backToLoginStep1(isV3) {
    yield put(loginActions.initLoginFlow());
    yield put(routerActions.replace(isV3 ? "/v3/login" : "/"));
    yield put(notificationActions.showNotification(i18nUtils.get("login.step1.wrong.credentials"), "error", ["login"]));
}

export function* handleRecoveryPassPreview({ username, exchangeToken }) {
    const pathname = yield select((s) => s.router?.location?.pathname || "");
    const isV3 = pathname.startsWith("/v3/");

    // Caso conocido: el exchange token es el centinela NOTOKENLOGIN (datos inválidos del step 1),
    // por lo que ni siquiera llamamos al backend.
    if (isSimulatedLoginToken(exchangeToken)) {
        yield* backToLoginStep1(isV3);
        return;
    }

    if (isV3) {
        yield put(recoveryPasswordActions.setAsyncPending(true));
    }
    try {
        const response = yield call(recovery.recoveryPassPreview, username, exchangeToken);

        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["loginStep2"]));
        } else {
            const { _exchangeToken } = response.data.data;
            const step1Path = isV3 ? "/v3/recoveryPassword/step1" : "/recoveryPassword/step1";

            yield put({ type: types.RECOVERY_PASS_PREVIEW_SUCCESS, exchangeToken: _exchangeToken });
            yield put(
                routerActions.replace({
                    pathname: step1Path,
                    state: {
                        fromLoginStep2: true,
                        preservedUsername: username,
                    },
                }),
            );
        }
    } catch (error) {
        // Si el backend responde 401 (p.ej. token de un usuario inexistente por anti-enumeración),
        // no se puede iniciar la recuperación: volvemos al step 1 de login en vez de dejar que el
        // 401 caiga en el handler global (que ante un body vacío puede fallar y dejar el botón inerte).
        if (error?.response?.status === 401) {
            yield* backToLoginStep1(isV3);
            return;
        }
        throw error;
    } finally {
        if (isV3) {
            yield put(recoveryPasswordActions.setAsyncPending(false));
        }
    }
}
